import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { LifeBuoy, Loader2 } from "lucide-react";
import { SupportDialog } from "@/components/support/SupportDialog";
import { ThreadMessages } from "@/routes/support";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { categoryLabel } from "@/lib/support";
import { HelpLink } from "@/components/help/HelpLink";
import { getMyTicket, getMyTickets, replyToMyTicket } from "@/lib/support.functions";

/** Statustext skriven för användaren, inte för supporten. */
function customerStatus(status: string): { label: string; waiting: boolean } {
  switch (status) {
    case "waiting":
      return { label: "Väntar på ditt svar", waiting: true };
    case "solved":
      return { label: "Löst", waiting: false };
    case "snoozed":
      return { label: "Pausat", waiting: false };
    case "spam":
      return { label: "Avslutat", waiting: false };
    default:
      return { label: "Vi tittar på det", waiting: false };
  }
}

export const Route = createFileRoute("/_authenticated/mina-arenden")({
  head: () => ({
    meta: [
      { title: "Mina ärenden — Sellavo" },
      {
        name: "description",
        content: "Följ dina supportärenden hos Sellavo och svara direkt i tråden.",
      },
      { property: "og:title", content: "Mina ärenden — Sellavo" },
      { property: "og:description", content: "Dina supportärenden hos Sellavo." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: MyTicketsPage,
});

function MyTicketsPage() {
  const fetchTickets = useServerFn(getMyTickets);
  const [openId, setOpenId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const { data: tickets, isPending } = useQuery({
    queryKey: ["my-tickets"],
    queryFn: () => fetchTickets({}),
  });

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground sm:text-3xl">
            Mina ärenden
          </h1>
          <p className="max-w-xl text-sm text-muted-foreground">
            Allt du skickat till supporten samlas här. Vi svarar både med mejl och i tråden nedan –
            öppna ärendet för att läsa och svara.
          </p>
        </div>
        <Button onClick={() => setNewOpen(true)}>Nytt ärende</Button>
      </div>

      {isPending ? (
        <p className="mt-8 text-muted-foreground">Laddar…</p>
      ) : !tickets?.length ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border p-8 text-center">
          <LifeBuoy className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 font-semibold">Du har inga ärenden</p>
          <p className="text-sm text-muted-foreground">
            Behöver du hjälp med något hör du av dig här.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-2">
          {tickets.map((t) => (
            <button
              key={t.id}
              onClick={() => setOpenId(openId === t.id ? null : t.id)}
              className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-accent/50"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 flex-1 truncate font-semibold">
                  #{t.ticket_no} · {t.subject}
                </span>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    customerStatus(t.status).waiting
                      ? "bg-primary/15 text-primary"
                      : "bg-secondary text-secondary-foreground"
                  }`}
                >
                  {customerStatus(t.status).waiting && (
                    <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle" />
                  )}
                  {customerStatus(t.status).label}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {categoryLabel(t.category)} ·{" "}
                {new Date(t.updated_at).toLocaleDateString("sv-SE", {
                  day: "numeric",
                  month: "short",
                })}
              </p>
            </button>
          ))}
        </div>
      )}

      {openId && <TicketThread id={openId} />}

      <div className="mt-8">
        <HelpLink label="Läs i hjälpcentret i stället" />
      </div>

      <SupportDialog open={newOpen} onOpenChange={setNewOpen} />
    </main>
  );
}

function TicketThread({ id }: { id: string }) {
  const fetchTicket = useServerFn(getMyTicket);
  const sendReply = useServerFn(replyToMyTicket);
  const qc = useQueryClient();
  const [body, setBody] = useState("");

  const { data, isPending } = useQuery({
    queryKey: ["my-ticket", id],
    queryFn: () => fetchTicket({ data: { id } }),
    refetchInterval: 30000,
  });

  const reply = useMutation({
    mutationFn: () => sendReply({ data: { id, body: body.trim() } }),
    onSuccess: () => {
      setBody("");
      toast.success("Svar skickat");
      void qc.invalidateQueries({ queryKey: ["my-ticket", id] });
      void qc.invalidateQueries({ queryKey: ["my-tickets"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isPending || !data) return <p className="mt-6 text-muted-foreground">Hämtar tråden…</p>;

  return (
    <section className="mt-6 rounded-2xl border border-border bg-card p-4 sm:p-6">
      <h2 className="font-heading text-lg font-bold">
        #{data.ticket.ticket_no} · {data.ticket.subject}
      </h2>
      <ThreadMessages messages={data.messages} />
      <div className="mt-4 space-y-2">
        <Textarea
          rows={3}
          value={body}
          placeholder="Skriv ett svar…"
          onChange={(e) => setBody(e.target.value)}
        />
        <Button disabled={body.trim().length < 1 || reply.isPending} onClick={() => reply.mutate()}>
          {reply.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Skicka svar
        </Button>
      </div>
    </section>
  );
}
